import React, { useMemo } from "react";
import { Box, Select, Text, Badge, HStack, Image, VStack, List, ListItem } from "@chakra-ui/react";

interface BrokerOption {
  value: string;
  name: string;
  isBeta: boolean;
  icon?: string;
  limitations?: {
    backfill: string;
    instruments: string;
    notes?: string;
  };
}

const BROKERS: BrokerOption[] = [
  { 
    value: "SCHWAB", 
    name: "Schwab", 
    isBeta: false, 
    icon: "https://cdn.wealthbee.io/main/brokers/broker_icons/schwab.png",
    limitations: {
      backfill: "Native integration",
      instruments: "Equities & options",
      notes: "Uses built-in Auto Sync. Connect below."
    }
  },
  { 
    value: "INTERACTIVE-BROKERS-FLEX", 
    name: "Interactive Brokers (IBKR)", 
    isBeta: false, 
    icon: "https://cdn.wealthbee.io/main/brokers/broker_icons/interactive-brokers.png",
    limitations: {
      backfill: "Native integration",
      instruments: "Equities & options",
      notes: "Uses built-in Auto Sync. Connect below."
    }
  },
  { 
    value: "FIDELITY", 
    name: "Fidelity", 
    isBeta: false, 
    icon: "https://cdn.wealthbee.io/main/brokers/broker_icons/fidelity.png",
    limitations: {
      backfill: "~24 months",
      instruments: "Equities & options",
      notes: "Older trades may require CSV upload. Some ACATS cost-basis reconciled later."
    }
  },
  { 
    value: "TASTYTRADE", 
    name: "Tastytrade", 
    isBeta: false, 
    icon: "https://cdn.wealthbee.io/main/brokers/broker_icons/tastytrade.png",
    limitations: {
      backfill: "Often full account history",
      instruments: "Equities & options",
      notes: "Excellent options coverage including exercises/assignments. Complex strategies consolidated in fills."
    }
  },
  { 
    value: "ETRADE", 
    name: "E*TRADE", 
    isBeta: true, 
    icon: "https://cdn.wealthbee.io/main/brokers/broker_icons/etrade.png",
    limitations: {
      backfill: "~24 months",
      instruments: "Equities & options",
      notes: "May need to unlink other third-party apps first. Older history available via staged imports."
    }
  },
  { 
    value: "ROBINHOOD", 
    name: "Robinhood", 
    isBeta: false, 
    icon: "https://cdn.wealthbee.io/main/brokers/broker_icons/robinhood.png",
    limitations: {
      backfill: "12–24 months",
      instruments: "Equities & options (no crypto)",
      notes: "Fractional shares supported. Older history may be partial."
    }
  },
  { 
    value: "WEALTHSIMPLETRADE", 
    name: "Wealthsimple Trade", 
    isBeta: true,
    limitations: {
      backfill: "Full account history",
      instruments: "Equities only",
      notes: "No options on this platform. Corporate action enrichment may lag for older periods."
    }
  },
  { 
    value: "WEBULL", 
    name: "Webull", 
    isBeta: true,
    limitations: {
      backfill: "~12 months",
      instruments: "Equities & options",
      notes: "Use staged date ranges for more history. Older transactions can be partial."
    }
  },
];

const BrokerSelector: React.FC<{
  broker: string;
  setBroker: (val: string) => void;
}> = ({ broker, setBroker }) => {
  const selectedBroker = BROKERS.find((b) => b.value === broker);

  const limitationsDisplay = useMemo(() => {
    if (!selectedBroker?.limitations) return null;
    return (
      <Box
        bg="gray.800"
        border="1px solid"
        borderColor="gray.600"
        borderRadius="md"
        p={3}
        mt={3}
      >
        <HStack mb={2} spacing={2}>
          <Text fontWeight={600} fontSize="sm" color="white">
            {selectedBroker.name}
          </Text>
          <Badge colorScheme="blue" variant="subtle" fontSize="xs">
            Coverage
          </Badge>
        </HStack>
        <List spacing={1} styleType="none">
          <ListItem>
            <HStack spacing={2}>
              <Box w="4px" h="4px" bg="blue.400" borderRadius="full" flexShrink={0} />
              <Text fontSize="sm" color="gray.300">
                {selectedBroker.limitations.backfill} backfill
              </Text>
            </HStack>
          </ListItem>
          <ListItem>
            <HStack spacing={2}>
              <Box w="4px" h="4px" bg="blue.400" borderRadius="full" flexShrink={0} />
              <Text fontSize="sm" color="gray.300">
                {selectedBroker.limitations.instruments}
              </Text>
            </HStack>
          </ListItem>
        </List>
        {selectedBroker.limitations.notes && (
          <Text fontSize="xs" color="gray.400" mt={2}>
            {selectedBroker.limitations.notes}
          </Text>
        )}
      </Box>
    );
  }, [selectedBroker]);

  return (
    <Box w="full">
      <HStack mb={2} spacing={2}>
        <Text fontWeight={600} color="white">Select a broker</Text>
        {selectedBroker?.isBeta && (
          <Badge colorScheme="orange" variant="subtle" fontSize="xs">Beta</Badge>
        )}
      </HStack>

      <HStack spacing={3} alignItems="center">
        <Select
          placeholder="Choose a broker..."
          value={broker}
          onChange={(e) => setBroker(e.target.value)}
          maxW="360px"
          bg="gray.800"
          borderColor="gray.600"
          color="white"
          _placeholder={{ color: "gray.400" }}
        >
          <optgroup label="Generally Available">
            {BROKERS.filter((b) => !b.isBeta).map((b) => (
              <option key={b.value} value={b.value}>
                {b.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="Beta">
            {BROKERS.filter((b) => b.isBeta).map((b) => (
              <option key={b.value} value={b.value}>
                {b.name}
              </option>
            ))}
          </optgroup>
        </Select>

        {selectedBroker?.icon && (
          <Image
            src={selectedBroker.icon}
            alt={selectedBroker.name}
            boxSize="32px"
            borderRadius="md"
            fallback={<Box boxSize="32px" bg="gray.700" borderRadius="md" />}
          />
        )}
      </HStack>

      <Text fontSize="xs" color="gray.500" mt={2}>
        Only read-only access will be granted. No trading permissions.
      </Text>

      {limitationsDisplay}
    </Box>
  );
};

export default BrokerSelector;


